import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "@amzn/meridian/button";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Alert from "@amzn/meridian/alert";
import { Space } from "../../space/Space";
import LoaderBox from "../../loader/LoaderBox";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import { useAppSelector } from "../../../app/store/hooks";
import { SelectNoticesProps } from "./types";
import {
    useNoticeSelection,
    useNoticeQueries,
    useUpdateAppealHandler,
    handleViewPdf,
} from "./hooks";
import { NoticeTable } from "./NoticeTable";
import { handleBackToProperty } from "../utils/utils";

export const SelectNotices: React.FC<SelectNoticesProps> = ({
    onNext,
    onPrevious,
    valueNotices,
    data,
}) => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [updateAppeal, { isLoading: isUpdating }] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);
    const { selected, onChangeRow } = useNoticeSelection(valueNotices);
    const { queries, isLoading, isError } = useNoticeQueries(valueNotices);

    const handleBack = () => {
        handleBackToProperty({
            data,
            propertyId: propertyId!,
            alias,
            updateAppeal,
            navigate,
        });
    };

    const handleUpdateAppeal = useUpdateAppealHandler(updateAppeal, data, alias, onNext);

    const handleNext = () => {
        const updatedNotices = valueNotices.map(notice => ({
            ...notice,
            enable: selected[notice.artifactId] || false,
        }));
        handleUpdateAppeal(updatedNotices);
    };

    if (isLoading || isUpdating) {
        return (
            <LoaderBox loadingText={isUpdating ? "Loading Appeal Data..." : "Loading notices..."} />
        );
    }

    if (isError) {
        return (
            <Box>
                <Space size={"l"} />
                <Alert type="error" size="medium">
                    An error occurred while loading notices. Please try again later.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Space size={"l"} />
            <Text type={"h500"}>Select Notices</Text>
            <Space size={"s"} />
            <Box type={"outline"} spacingInset={"500"}>
                <Text type={"h300"}> Select Value Notices</Text>
                <Text>
                    Selected value notices will be included in the appeals package. Upload notices
                    if they are not in the list.
                </Text>

                <Space size={"s"} />
                <Alert type={"theme"} size={"medium"}>
                    For mails not in the list, upload the PDF by clicking the 'Add mail' button.
                </Alert>
                <Space size={"s"} />
                {valueNotices.length === 0 ? (
                    <Text>No notices available</Text>
                ) : (
                    <NoticeTable
                        valueNotices={valueNotices}
                        queries={queries}
                        selected={selected}
                        onChangeRow={onChangeRow}
                        onViewPdf={handleViewPdf}
                    />
                )}
            </Box>

            <Space size={"s"} />
            <Row alignmentHorizontal={"end"} spacingInset={"200"}>
                <Button type={"link"} size={"large"} onClick={handleBack}>
                    Back to Property
                </Button>
                <Button size={"large"} onClick={onPrevious} type={"secondary"}>
                    Back
                </Button>
                <Button size={"large"} onClick={handleNext}>
                    Next
                </Button>
            </Row>
        </Box>
    );
};
